import { X } from "lucide-react"
import { useEffect, useState } from "react"
import type { Client } from "../types"

type AddProjectModalProps = {
  isOpen: boolean
  onClose: () => void
  onSave: (project: { name: string; clientId: string; status: string; budgetHours: number; dueDate: string }) => void
}

const AddProjectModal = ({ isOpen, onClose, onSave }: AddProjectModalProps) => {
  const [clients, setClients] = useState<Client[]>([])
  const [formData, setFormData] = useState({
    name: "",
    clientId: "",
    status: "Planned",
    budgetHours: 0,
    dueDate: "",
  })
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (isOpen) {
        fetch("/api/clients")
            .then(res => res.json())
            .then(data => {
                if (data.clients) setClients(data.clients)
            })
            .catch(console.error)
    }
  }, [isOpen])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    try {
      await onSave(formData)
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm p-4">
      <div className="w-full max-w-md rounded-[24px] bg-white p-6 shadow-xl animate-in fade-in zoom-in-95">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-slate-900">New Project</h2>
          <button onClick={onClose} className="rounded-full p-2 hover:bg-slate-100 transition">
            <X className="h-5 w-5 text-stone" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Project Name</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full rounded-xl border border-line px-4 py-2.5 text-sm focus:border-moss focus:outline-none focus:ring-4 focus:ring-moss/10"
              placeholder="e.g. Q1 Marketing Campaign"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Client</label>
            <select
              required
              value={formData.clientId}
              onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
              className="w-full rounded-xl border border-line px-4 py-2.5 text-sm focus:border-moss focus:outline-none focus:ring-4 focus:ring-moss/10 bg-white"
            >
              <option value="">Select a client...</option>
              {clients.map(client => (
                  <option key={client.id} value={client.id}>{client.name}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
                <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full rounded-xl border border-line px-4 py-2.5 text-sm focus:border-moss focus:outline-none focus:ring-4 focus:ring-moss/10 bg-white"
                >
                <option value="Planned">Planned</option>
                <option value="Active">Active</option>
                <option value="Review">Review</option>
                <option value="Complete">Complete</option>
                </select>
            </div>
            <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Budget (Hours)</label>
                <input
                type="number"
                min="0"
                value={formData.budgetHours}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    budgetHours: e.target.value ? Number.parseFloat(e.target.value) : 0,
                  })
                }
                className="w-full rounded-xl border border-line px-4 py-2.5 text-sm focus:border-moss focus:outline-none focus:ring-4 focus:ring-moss/10"
                />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Due Date</label>
            <input
              type="date"
              value={formData.dueDate}
              onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
              className="w-full rounded-xl border border-line px-4 py-2.5 text-sm focus:border-moss focus:outline-none focus:ring-4 focus:ring-moss/10"
            />
          </div>

          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-full border border-line px-4 py-2.5 text-sm font-semibold text-stone hover:bg-slate-50 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 rounded-full bg-moss px-4 py-2.5 text-sm font-semibold text-white shadow-soft hover:bg-moss/90 transition disabled:opacity-50"
            >
              {loading ? "Creating..." : "Create Project"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AddProjectModal
